/**
 * PrivateAIChatWidget — B2C Spesialisert juridisk AI-assistent
 * Utløses av "Spør juridisk assistent"-knappen i modul-dashboards
 * Privacy-First: Sesjon slettes ved lukking
 * 
 * v2.0: Aktiv Medhjelper — auto-trigger ved dokument-opplasting
 *       + "Åpne i dokumentgenerator"-knapp for dokument-injeksjon
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  X, Send, Loader2, Sparkles, AlertCircle, Scale, FileText, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const B2C_ROUTE_MAP = {
  'arbeidsrett':      { key: 'arbeidsrett-privat', label: 'Arbeidsrett' },
  'forvaltningsrett': { key: 'forvaltningsrett',   label: 'Forvaltningsrett' },
  'eiendomsrett':     { key: 'eiendomsrett',       label: 'Eiendomsrett' },
  'arverett':         { key: 'arverett',            label: 'Arverett' },
  'trygderett':       { key: 'trygderett',          label: 'Trygderett' },
  'forbrukerrett':    { key: 'forbrukerrett',       label: 'Forbrukerrett' },
  'husleierett':      { key: 'eiendomsrett',        label: 'Husleierett' },
  'barnevern':        { key: 'barnevern-master',    label: 'Barnevern' },
};

const WELCOME = {
  'arbeidsrett-privat': 'Hei! Jeg er din ekspert på Arbeidsrett. Jeg kan hjelpe deg med oppsigelse, arbeidsavtaler og dine rettigheter på arbeidsplassen. Hva kan jeg hjelpe deg med i dag?',
  'forvaltningsrett':   'Hei! Jeg er din ekspert på Forvaltningsrett. Jeg kan hjelpe deg med klager på vedtak, partsinnsyn og saksbehandlingsfeil. Hva kan jeg hjelpe deg med i dag?',
  'eiendomsrett':       'Hei! Jeg er din ekspert på Eiendomsrett. Jeg kan hjelpe deg med boligkjøp, nabotvister og leieforhold. Hva kan jeg hjelpe deg med i dag?',
  'arverett':           'Hei! Jeg er din ekspert på Arverett. Jeg kan hjelpe deg med testament, fremtidsfullmakt og skifte. Hva kan jeg hjelpe deg med i dag?',
  'trygderett':         'Hei! Jeg er din ekspert på Trygderett. Jeg kan hjelpe deg med NAV-klager, ytelser og rettigheter i folketrygden. Hva kan jeg hjelpe deg med i dag?',
  'forbrukerrett':      'Hei! Jeg er din ekspert på Forbrukerrett. Jeg kan hjelpe deg med reklamasjon, angrerett og netthandel. Hva kan jeg hjelpe deg med i dag?',
  'barnevern-master':   'Hei! Jeg er din ekspert på Barnevern. Hva kan jeg hjelpe deg med i dag?',
};

function detectModule(pathname) {
  for (const [segment, config] of Object.entries(B2C_ROUTE_MAP)) {
    if (pathname.includes(segment)) return config;
  }
  return { key: null, label: 'Juridisk rådgivning' };
}

const ChatBubble = React.memo(function ChatBubble({ msg, onOpenGenerator, moduleId }) {
  const isUser = msg.role === 'user';
  // Show generator button if: has documentId OR AI suggests opening the generator
  const suggestsGenerator = !isUser && !msg.documentId && /dokumentgenerator|lage et (brev|klage|motsvar|reklamasjon)|åpne.*generator/i.test(msg.text || '');
  const showGeneratorBtn = !isUser && (msg.documentId || suggestsGenerator);
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`} data-testid={`chat-msg-${msg.role}`}>
      {!isUser && (
        <div className="w-6 h-6 rounded-full bg-[var(--copper)]/20 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
          <Scale className="w-3.5 h-3.5 text-[var(--copper)]" />
        </div>
      )}
      <div className={`max-w-[85%] ${
        isUser
          ? 'px-3.5 py-2.5 rounded-2xl rounded-br-md bg-[var(--copper)] text-white'
          : ''
      }`}>
        <div className={`${!isUser ? 'px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-[var(--text-main)]' : ''}`}>
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{msg.text}</div>
          {msg.model && (
            <div className="mt-1.5 text-[10px] opacity-50 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" />
              {msg.model}
            </div>
          )}
        </div>
        
        {/* "Åpne i dokumentgenerator" button */}
        {showGeneratorBtn && (
          <button
            onClick={() => onOpenGenerator(msg.documentId, msg.moduleId || moduleId)}
            className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 bg-[var(--copper)]/10 hover:bg-[var(--copper)]/20 border border-[var(--copper)]/30 rounded-lg text-xs font-medium text-[var(--copper)] transition-all"
            data-testid="open-in-generator-btn"
          >
            <FileText className="w-3.5 h-3.5" />
            Åpne i dokumentgenerator
            <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
});

export default function PrivateAIChatWidget() {
  const { user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [moduleCtx, setModuleCtx] = useState(() => detectModule(location.pathname));
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Listen for "open-ai-chat" custom event from ModuleControlPanel
  useEffect(() => {
    const handler = (e) => {
      const moduleId = e.detail?.moduleId;
      if (moduleId) {
        const mapped = B2C_ROUTE_MAP[moduleId] || { key: moduleId, label: moduleId };
        setModuleCtx(mapped);
      } else {
        setModuleCtx(detectModule(location.pathname));
      }
      openChat();
    };
    window.addEventListener('open-ai-chat', handler);
    return () => window.removeEventListener('open-ai-chat', handler);
  }, [location.pathname]);

  // Listen for "document-uploaded" custom event — Aktiv Medhjelper
  useEffect(() => {
    const handler = (e) => {
      const { document: doc, moduleId } = e.detail || {};
      if (!doc || !token) return;

      // Set module context
      const mapped = B2C_ROUTE_MAP[moduleId] || { key: moduleId, label: moduleId };
      setModuleCtx(mapped);

      // Open chat and auto-send document context
      setOpen(true);
      setError(null);

      // Create session and send document context
      (async () => {
        try {
          const res = await axios.post(`${API}/ai-chat/new-session`, {}, { headers });
          const newSessionId = res.data.session_id;
          setSessionId(newSessionId);

          // Add a system-like message showing the document was detected
          setMessages([{
            role: 'assistant',
            text: `Jeg ser du har lastet opp "${doc.filename}". Analyserer dokumentet mot din sak...`,
          }]);

          setSending(true);
          const chatRes = await axios.post(`${API}/ai-chat/message`, {
            session_id: newSessionId,
            message: `Analyser det opplastede dokumentet "${doc.filename}"`,
            module_context: mapped.key,
            document_context: {
              filename: doc.filename,
              module_id: moduleId,
              analysis: doc.analysis || '',
              document_id: doc.id,
            }
          }, { headers });

          setMessages([{
            role: 'assistant',
            text: chatRes.data.response,
            model: chatRes.data.model_used,
            documentId: doc.id,
            moduleId: moduleId,
          }]);
        } catch {
          setError('Kunne ikke analysere dokumentet automatisk');
        }
        setSending(false);
      })();
    };

    window.addEventListener('document-uploaded', handler);
    return () => window.removeEventListener('document-uploaded', handler);
  }, [token, headers]);

  // Update module context on route change (if widget stays open)
  useEffect(() => {
    if (!open) {
      setModuleCtx(detectModule(location.pathname));
    }
  }, [location.pathname, open]);

  const closeChat = useCallback(async () => {
    if (sessionId) {
      try { await axios.delete(`${API}/ai-chat/session/${sessionId}`, { headers }); } catch {}
    }
    setOpen(false);
    setMessages([]);
    setSessionId(null);
    setInput('');
    setError(null);
  }, [sessionId, headers]);

  const openChat = useCallback(async () => {
    setOpen(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/ai-chat/new-session`, {}, { headers });
      setSessionId(res.data.session_id);
    } catch {
      setError('Kunne ikke starte chat-sesjon');
    }
  }, [headers]);

  // Set welcome message when moduleCtx and session are ready
  useEffect(() => {
    if (open && sessionId && messages.length === 0 && moduleCtx.key) {
      const welcome = WELCOME[moduleCtx.key];
      if (welcome) {
        setMessages([{ role: 'assistant', text: welcome }]);
      }
    }
  }, [open, sessionId, moduleCtx.key]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setInput('');
    setError(null);
    setMessages(prev => [...prev, { role: 'user', text }]);
    setSending(true);

    try {
      const response = await fetch(`${API}/ai-chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          module_context: moduleCtx.key,
        }),
      });

      if (!response.ok) throw new Error('Stream request failed');

      // Add empty assistant message that we'll stream into
      setMessages(prev => [...prev, { role: 'assistant', text: '', streaming: true }]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, text: last.text + data.content };
                }
                return updated;
              });
            } else if (data.type === 'done') {
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, streaming: false };
                }
                return updated;
              });
            } else if (data.type === 'error') {
              setError(data.error);
            }
          } catch {}
        }
      }
    } catch (err) {
      const detail = err.message || 'Noe gikk galt. Prøv igjen.';
      setError(detail);
    }
    setSending(false);
  }, [input, sending, sessionId, moduleCtx.key, token]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // "Åpne i dokumentgenerator" handler — prefills and navigates
  const handleOpenGenerator = useCallback(async (documentId, moduleId) => {
    if (documentId) {
      // Has a specific document — fetch prefill from backend
      try {
        const res = await axios.get(`${API}/module-documents/${documentId}/prefill?user_id=${user?.id}`);
        const prefill = res.data.prefill;
        sessionStorage.setItem('rettverk_doc_prefill', JSON.stringify(prefill));
        closeChat();
        navigate('/documents', { state: { prefill, fromChat: true } });
        return;
      } catch {
        // Fall through to basic navigation
      }
    }
    
    // No document or prefill failed — build prefill from chat context
    const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
    const chatContext = lastAssistantMsg?.text || '';
    const prefill = {
      doc_type: /klage/i.test(chatContext) ? 'klage' : /motsvar/i.test(chatContext) ? 'formelt_brev' : /reklamasjon/i.test(chatContext) ? 'klage' : 'formelt_brev',
      tittel: '',
      juridisk_argumentasjon: '',
      motpart_navn: '',
      dato: '',
      saksnummer: '',
    };
    sessionStorage.setItem('rettverk_doc_prefill', JSON.stringify(prefill));
    closeChat();
    navigate('/documents', { state: { prefill, fromChat: true } });
  }, [user?.id, navigate, closeChat, messages]);

  if (!token) return null;

  return (
    <>
      {/* Floating trigger button (visible when chat is closed) */}
      {!open && (
        <button
          onClick={() => {
            setModuleCtx(detectModule(location.pathname));
            openChat();
          }}
          className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-[var(--copper)] text-white shadow-lg shadow-[var(--copper)]/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
          data-testid="ai-chat-toggle"
          title="Spør juridisk assistent"
        >
          <Scale className="w-6 h-6" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[400px] h-[560px] bg-[var(--bg-deep)] border border-[var(--border-subtle)] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="ai-chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[var(--copper)]/20 flex items-center justify-center">
                <Scale className="w-4 h-4 text-[var(--copper)]" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-main)]">RettVerk AI</h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-[10px] text-[var(--text-muted)]">{moduleCtx.label}</span>
                </div>
              </div>
            </div>
            <button
              onClick={closeChat}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-highlight)] text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
              data-testid="ai-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="ai-chat-messages">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <div className="w-12 h-12 rounded-xl bg-[var(--copper)]/10 flex items-center justify-center mb-3">
                  <Sparkles className="w-6 h-6 text-[var(--copper)]" />
                </div>
                <p className="text-sm text-[var(--text-main)] font-medium mb-1">Juridisk AI-assistent</p>
                <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                  Spesialisert for <span className="font-medium text-[var(--copper)]">{moduleCtx.label}</span>.
                  Still spørsmål om dine rettigheter, frister eller prosesser.
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <ChatBubble key={i} msg={msg} onOpenGenerator={handleOpenGenerator} moduleId={moduleCtx.key} />
            ))}

            {sending && (
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-[var(--copper)]/20 flex items-center justify-center">
                  <Scale className="w-3.5 h-3.5 text-[var(--copper)]" />
                </div>
                <div className="px-3.5 py-2.5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-2xl rounded-bl-md">
                  <Loader2 className="w-4 h-4 animate-spin text-[var(--copper)]" />
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg mb-3">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-xs text-red-300">{error}</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input + Disclaimer */}
          <div className="px-3 py-3 border-t border-[var(--border-subtle)] bg-[var(--bg-surface)]">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Skriv en melding..."
                rows={1}
                className="flex-1 px-3 py-2 bg-[var(--bg-deep)] border border-[var(--border-subtle)] rounded-xl text-sm text-[var(--text-main)] placeholder-[var(--text-muted)]/50 resize-none focus:outline-none focus:border-[var(--copper)]/50 max-h-24"
                disabled={sending || !sessionId}
                data-testid="ai-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim() || !sessionId}
                className="p-2.5 rounded-xl bg-[var(--copper)] text-white disabled:opacity-40 hover:brightness-110 transition-all flex-shrink-0"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[9px] text-[var(--text-muted)]/60 mt-1.5 px-1" data-testid="ai-chat-disclaimer">
              RettVerk AI gir juridisk veiledning. Ved kritiske behov, søk profesjonell advokathjelp.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
